// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * CareComm Context
 *
 * Global state management for the CareComm omnichannel communication module.
 * Handles messaging (SMS/WhatsApp via Twilio), voice/video calls (via Agora),
 * and real-time notifications.
 *
 * All communications are stored as FHIR Communication resources and linked
 * to patients and encounters.
 *
 * ## Architecture
 *
 * ### Twilio (SMS/WhatsApp) - Per-Tenant Subaccounts
 * - Master Twilio account holds subaccounts and central governance
 * - Each tenant (Medplum project) has its own:
 *   - Twilio Subaccount (SID + auth token stored in Project secrets)
 *   - Messaging Service (with dedicated phone numbers)
 *   - WhatsApp sender (if enabled)
 * - Benefits:
 *   - Tenant isolation / blast radius containment
 *   - Clear billing attribution per tenant
 *   - Independent opt-out handling and compliance
 *   - Can suspend one tenant without affecting others
 * - Webhook endpoint is platform-wide; bot resolves tenant from inbound "To" number
 *
 * ### Agora (Voice/Video) - Platform-wide with Token Isolation
 * - One Agora App ID shared across all tenants
 * - Isolation achieved at token layer, not credential layer
 * - Channel naming: "{projectId}:{contextId}:{timestamp}"
 * - Bot validates authorization before issuing short-TTL tokens
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useMedplum } from '@medplum/react';
import { useMediaQuery } from '@mantine/hooks';
import type { Communication, Patient, Encounter, Practitioner } from '@medplum/fhirtypes';
import { agoraService } from '../services/agoraService';
import { useOrganization } from './OrganizationContext';
import {
  getCareCommSettings,
  type CareCommSettingsData,
  type CareCommChannel,
  CARECOMM_CATEGORY_SYSTEM,
  CARECOMM_EXTENSION_URLS,
} from '../utils/organizationSettings';
import {
  CARECOMM_OUTCOMES,
  patchCommunicationOnUnload,
  patchCommunicationOutcome,
} from '../utils/callCleanup';
import { getTenantConfig } from '../config/tenant';
import { showErrorNotification, showSuccessNotification } from '../utils/notifications';
import {
  getSmartEncounterLink,
  getAutoLinkedEncounterId,
  type SmartEncounterLinkResult,
} from '../utils/encounterLinking';
import type { Reference } from '@medplum/fhirtypes';
import { getReferenceString, formatHumanName } from '@medplum/core';

// ============================================
// Routing Contract (Twilio Hybrid Model)
// ============================================

/**
 * Deterministic Routing Contract for Twilio Messages
 *
 * ## Configuration Modes
 * - TENANT_WIDE: Single-org tenants. One Twilio config for the entire tenant.
 * - ORG_SCOPED: Multi-org tenants. Each org has its own Twilio subaccount/numbers.
 *
 * ## Inbound Routing (Twilio → Bot)
 * Route by the inbound "To" number (or MessagingServiceSid).
 *
 * Algorithm:
 * 1. Identify projectId (tenant) from webhook endpoint mapping
 * 2. Read twilioConfigScope from tenant config
 * 3. Resolve destination:
 *    - If TENANT_WIDE: route to tenant's single org
 *    - If ORG_SCOPED: lookup (projectId, toNumber) → organizationId
 * 4. Create FHIR Communication with:
 *    - sender = Patient
 *    - recipient = Organization/CareTeam/Practitioner
 *    - encounter = linked if known, else thread container
 *
 * ## Outbound Routing (Provider App/Bot → Twilio)
 * Use the Organization from clinical context (Encounter/Appointment):
 * - If ORG_SCOPED: send using that org's Twilio config
 * - If TENANT_WIDE: send using tenant's shared config
 *
 * ## Guardrails
 * A: Require number ownership mapping
 * B: Enforce uniqueness (no duplicate To numbers across orgs in ORG_SCOPED)
 * C: Multi-org tenants must use ORG_SCOPED mode
 */

// ============================================
// Channel Naming Policy (Platform-wide Agora)
// ============================================

/**
 * Generate a secure, tenant-scoped Agora channel name.
 *
 * Platform-wide Agora Configuration:
 * - One Agora App ID is shared across all tenants
 * - Isolation is achieved at the token layer, not the credential layer
 * - Tokens are generated server-side with strict authorization checks
 *
 * Channel naming policy for tenant isolation:
 * Format: "{projectId}:{contextId}:{timestamp}"
 *
 * This ensures:
 * - Channels are scoped to the Medplum project (tenant)
 * - Channels are tied to specific encounters or patients
 * - Channels are unique (timestamp prevents collisions)
 *
 * The bot MUST verify before issuing tokens:
 * 1. User belongs to the Medplum project
 * 2. Encounter/Patient exists in that project
 * 3. User has correct role (Practitioner vs Patient)
 * 4. Short TTL (5-15 minutes) with refresh as needed
 */
function generateChannelName(projectId: string, encounterId: string | undefined, patientId: string): string {
  const timestamp = Date.now();
  // Use encounter ID if available, otherwise use patient ID as fallback
  const contextId = encounterId || `p_${patientId}`;
  return `${projectId}:${contextId}:${timestamp}`;
}

/**
 * Parse a channel name to extract its components
 * Used by bots for validation
 */
export function parseChannelName(channelName: string): { projectId: string; contextId: string; timestamp: number } | null {
  const parts = channelName.split(':');
  if (parts.length !== 3) return null;
  return {
    projectId: parts[0],
    contextId: parts[1],
    timestamp: parseInt(parts[2], 10),
  };
}

// ============================================
// Types
// ============================================

export type CallState = 'idle' | 'connecting' | 'ringing' | 'connected' | 'ended';
export type CallDisplayMode = 'fullscreen' | 'pip' | 'minimized';

export interface ActiveCall {
  /** Agora channel ID */
  channelId: string;
  /** Patient on the call */
  patient: Patient;
  /** Linked encounter (if any) */
  encounterId?: string;
  /** Call type */
  type: 'voice-call' | 'video-call';
  /** Call state */
  state: CallState;
  /** Call start time */
  startTime: number;
  /** Duration in seconds */
  duration: number;
  /** Is microphone muted */
  isMuted: boolean;
  /** Is camera off (video calls only) */
  isCameraOff: boolean;
  /** Is screen sharing active */
  isScreenSharing: boolean;
  /** Display mode for the call UI */
  displayMode: CallDisplayMode;
  /** ID of the in-progress Communication signaling record (from the bot). */
  communicationId?: string;
}

export interface IncomingCall {
  /** Agora channel ID */
  channelId: string;
  /** Caller patient */
  patient: Patient;
  /** Call type */
  type: 'voice-call' | 'video-call';
  /** Ring start time */
  ringStartTime: number;
}

export interface CareCommMessage {
  /** Communication resource ID */
  id: string;
  /** Channel type */
  channel: CareCommChannel;
  /** Message content */
  content: string;
  /** Sender reference */
  senderRef: string;
  /** Sender display name */
  senderName: string;
  /** Is this message from the patient (inbound) */
  isInbound: boolean;
  /** Sent timestamp */
  sentAt: string;
  /** Read status */
  isRead: boolean;
}

export interface CareCommConversation {
  /** Patient for this conversation */
  patient: Patient;
  /** Most recent message */
  lastMessage?: CareCommMessage;
  /** Unread message count */
  unreadCount: number;
  /** All messages in thread */
  messages: CareCommMessage[];
  /** Last activity timestamp */
  lastActivityAt: string;
}

// ============================================
// Context Interface
// ============================================

interface CareCommContextValue {
  // Settings
  settings: CareCommSettingsData;
  isEnabled: boolean;

  // Panel state
  isPanelOpen: boolean;
  openPanel: (patientId?: string) => void;
  closePanel: () => void;
  selectedPatientId: string | null;
  setSelectedPatientId: (patientId: string | null) => void;

  // Messaging state
  conversations: CareCommConversation[];
  unreadCount: number;
  isLoadingConversations: boolean;
  refreshConversations: () => Promise<void>;

  // Messaging actions
  sendMessage: (
    patientId: string,
    channel: 'sms' | 'whatsapp',
    message: string,
    encounterId?: string
  ) => Promise<void>;
  sendChatMessage: (
    patientId: string,
    message: string,
    threadRef?: Reference<Communication>,
    encounterId?: string
  ) => Promise<Communication>;
  markAsRead: (communicationIds: string[]) => Promise<void>;

  // Smart encounter linking
  getEncounterLink: (patientId: string, explicitEncounterId?: string) => Promise<SmartEncounterLinkResult>;

  // Call state
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;

  // Call actions
  startCall: (
    patient: Patient,
    type: 'voice-call' | 'video-call',
    encounterId?: string
  ) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => Promise<void>;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;

  // PiP mode actions
  setCallDisplayMode: (mode: CallDisplayMode) => void;
  enterPiPMode: () => void;
  exitPiPMode: () => void;
  minimizeCall: () => void;

  // Current practitioner
  currentPractitioner: Practitioner | null;
}

const CareCommContext = createContext<CareCommContextValue | undefined>(undefined);

// ============================================
// Provider Component
// ============================================

interface CareCommProviderProps {
  children: ReactNode;
}

export function CareCommProvider({ children }: CareCommProviderProps): JSX.Element {
  console.log('[CareComm] CareCommProvider rendering');
  const medplum = useMedplum();
  const location = useLocation();
  const { currentOrganization } = useOrganization();

  // Check if mobile for PiP mode restrictions
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Settings state
  const [settings, setSettings] = useState<CareCommSettingsData>({
    enabled: false,
    smsEnabled: false,
    whatsappEnabled: false,
    voiceEnabled: false,
    videoEnabled: false,
    twilioConfigured: false,
    agoraConfigured: false,
    autoCreateEncounter: true,
    defaultGreeting: '',
    recordingEnabled: false,
  });

  // Panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  // Messaging state
  const [conversations, setConversations] = useState<CareCommConversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);

  // Call state
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);

  // Current practitioner
  const [currentPractitioner, setCurrentPractitioner] = useState<Practitioner | null>(null);

  // Refs for call timer
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Calculate total unread count
  const unreadCount = useMemo(() => {
    return conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
  }, [conversations]);

  // Check if CareComm is enabled
  const isEnabled = settings.enabled && (
    settings.smsEnabled ||
    settings.whatsappEnabled ||
    settings.voiceEnabled ||
    settings.videoEnabled
  );

  // Load settings when organization changes
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentOrganization?.id) return;

      try {
        // Load organization-level settings
        const org = await medplum.readResource('Organization', currentOrganization.id);
        const loadedSettings = getCareCommSettings(org);

        // Check tenant-level config via carecomm-config bot
        // This checks if Twilio/Agora secrets are configured in the Medplum project
        let twilioConfigured = loadedSettings.twilioConfigured;
        let agoraConfigured = loadedSettings.agoraConfigured;

        try {
          const tenantConfig = getTenantConfig();
          const configResult = await medplum.executeBot(
            { system: tenantConfig.botIdentifierSystem, value: 'carecomm-config' },
            { action: 'getStatus' },
            'application/json'
          );

          if (configResult && typeof configResult === 'object') {
            twilioConfigured = configResult.twilioConfigured ?? twilioConfigured;
            agoraConfigured = configResult.agoraConfigured ?? agoraConfigured;

            // If tenant has CareComm configured (Twilio/Agora secrets exist),
            // respect organization-level settings from the extension.
            // Priority: Organization extension settings > Bot defaults
            if (twilioConfigured || agoraConfigured) {
              // For SMS/WhatsApp: use org settings if explicitly set, otherwise use bot defaults
              const smsEnabled = twilioConfigured && (loadedSettings.smsEnabled ?? configResult.smsEnabled ?? true);
              const whatsappEnabled = twilioConfigured && (loadedSettings.whatsappEnabled ?? configResult.whatsappEnabled ?? false);
              // For Voice/Video: use org settings if explicitly set, otherwise use bot defaults
              const voiceEnabled = agoraConfigured && (loadedSettings.voiceEnabled ?? configResult.voiceEnabled ?? true);
              const videoEnabled = agoraConfigured && (loadedSettings.videoEnabled ?? configResult.videoEnabled ?? true);

              console.log('CareComm configured at tenant level - applying org settings:', {
                twilioConfigured,
                agoraConfigured,
                smsEnabled,
                whatsappEnabled,
                voiceEnabled,
                videoEnabled,
                orgSettings: {
                  sms: loadedSettings.smsEnabled,
                  whatsapp: loadedSettings.whatsappEnabled,
                  voice: loadedSettings.voiceEnabled,
                  video: loadedSettings.videoEnabled,
                },
              });
              setSettings({
                ...loadedSettings,
                enabled: true,
                twilioConfigured,
                agoraConfigured,
                smsEnabled,
                whatsappEnabled,
                voiceEnabled,
                videoEnabled,
              });
              return;
            }
          }
        } catch (botError) {
          // Bot may not exist yet - fall back to organization extension values
          console.warn('CareComm config bot not available:', botError);
        }

        // No tenant-level config - use organization settings only
        setSettings({
          ...loadedSettings,
          twilioConfigured,
          agoraConfigured,
        });
      } catch (error) {
        console.error('Failed to load CareComm settings:', error);
      }
    };

    loadSettings();
  }, [currentOrganization?.id, medplum]);

  // Load current practitioner
  useEffect(() => {
    const profile = medplum.getProfile();
    if (profile?.resourceType === 'Practitioner') {
      setCurrentPractitioner(profile as Practitioner);
    }
  }, [medplum]);

  // ============================================
  // Panel Actions
  // ============================================

  const openPanel = useCallback((patientId?: string) => {
    setIsPanelOpen(true);
    if (patientId) {
      setSelectedPatientId(patientId);
    }
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // ============================================
  // Message Fetching
  // ============================================

  const fetchConversations = useCallback(async () => {
    console.log('[CareComm] fetchConversations called, isEnabled:', isEnabled);
    if (!isEnabled) {
      console.log('[CareComm] Skipping fetch - CareComm not enabled');
      return;
    }

    setIsLoadingConversations(true);
    try {
      // Fetch recent Communications for this organization
      // Include both CareComm category and standard notification category for backwards compatibility
      // This ensures we show messages from both old and new bot versions
      const [carecommComms, notificationComms] = await Promise.all([
        medplum.searchResources('Communication', {
          _count: '100',
          _sort: '-sent',
          'category': `${CARECOMM_CATEGORY_SYSTEM}|`,
        }),
        medplum.searchResources('Communication', {
          _count: '100',
          _sort: '-sent',
          'category': 'http://terminology.hl7.org/CodeSystem/communication-category|notification',
        }),
      ]);

      // Merge and dedupe by ID
      const commMap = new Map<string, Communication>();
      for (const comm of [...carecommComms, ...notificationComms]) {
        if (comm.id && !commMap.has(comm.id)) {
          commMap.set(comm.id, comm);
        }
      }
      const communications = Array.from(commMap.values());

      // Debug: Log what we found
      console.log('[CareComm] Fetched communications:', {
        carecommCount: carecommComms.length,
        notificationCount: notificationComms.length,
        totalUnique: communications.length,
      });

      // Group by patient
      const patientMap = new Map<string, {
        patient: Patient | null;
        messages: CareCommMessage[];
        unreadCount: number;
      }>();

      for (const comm of communications) {
        const patientRef = comm.subject?.reference;
        if (!patientRef || !patientRef.startsWith('Patient/')) continue;

        const patientId = patientRef.replace('Patient/', '');

        if (!patientMap.has(patientId)) {
          patientMap.set(patientId, {
            patient: null,
            messages: [],
            unreadCount: 0,
          });
        }

        const group = patientMap.get(patientId)!;

        // Parse message
        // Handle both CareComm categories (sms, whatsapp) and standard notification category
        const categoryCode = comm.category?.[0]?.coding?.[0]?.code;
        const channel: CareCommChannel = (categoryCode === 'sms' || categoryCode === 'whatsapp')
          ? categoryCode
          : (comm.medium?.[0]?.text?.toLowerCase() === 'whatsapp' ? 'whatsapp' : 'sms');

        // Determine direction from extension or sender reference
        const directionExt = comm.extension?.find(
          (e) => e.url === CARECOMM_EXTENSION_URLS.messageDirection
        );
        // If no direction extension, infer from sender (Patient = inbound, Practitioner = outbound)
        const senderIsPatient = comm.sender?.reference?.startsWith('Patient/') ?? false;
        const isInbound = directionExt?.valueCode === 'inbound' || senderIsPatient;

        // Extract message content - handle multiple payload formats
        // FHIR Communication payload can be: contentString, contentAttachment, or contentReference
        let messageContent = '';
        const payload = comm.payload?.[0];
        if (payload) {
          if (payload.contentString) {
            messageContent = payload.contentString;
          } else if (payload.contentAttachment?.data) {
            // Base64 encoded text
            try {
              messageContent = atob(payload.contentAttachment.data);
            } catch {
              messageContent = '[Attachment]';
            }
          } else if (payload.contentAttachment?.url) {
            messageContent = '[Attachment]';
          } else if (payload.contentReference?.reference) {
            messageContent = '[Reference: ' + payload.contentReference.reference + ']';
          }
        }

        const message: CareCommMessage = {
          id: comm.id || '',
          channel,
          content: messageContent,
          senderRef: comm.sender?.reference || '',
          senderName: comm.sender?.display || (isInbound ? 'Patient' : 'Provider'),
          isInbound,
          sentAt: comm.sent || new Date().toISOString(),
          isRead: comm.status === 'completed',
        };

        group.messages.push(message);

        if (!message.isRead && isInbound) {
          group.unreadCount++;
        }
      }

      // Fetch patient details for each conversation
      const conversationList: CareCommConversation[] = [];

      for (const [patientId, group] of patientMap) {
        try {
          const patient = await medplum.readResource('Patient', patientId);
          group.patient = patient;

          // Sort messages by sent time
          group.messages.sort(
            (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
          );

          conversationList.push({
            patient,
            lastMessage: group.messages[group.messages.length - 1],
            unreadCount: group.unreadCount,
            messages: group.messages,
            lastActivityAt: group.messages[group.messages.length - 1]?.sentAt || new Date().toISOString(),
          });
        } catch {
          // Skip if patient not found
          console.warn(`Patient ${patientId} not found`);
        }
      }

      // Sort by last activity
      conversationList.sort(
        (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
      );

      setConversations(conversationList);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [isEnabled, medplum]);

  // Initial fetch and polling
  useEffect(() => {
    if (!isEnabled) return;

    // Initial fetch
    fetchConversations();

    // Poll every 10 seconds for new messages
    pollingIntervalRef.current = setInterval(fetchConversations, 10000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isEnabled, fetchConversations]);

  // ============================================
  // Message Actions
  // ============================================

  const sendMessage = useCallback(async (
    patientId: string,
    channel: 'sms' | 'whatsapp',
    message: string,
    encounterId?: string
  ) => {
    if (!currentPractitioner?.id) {
      showErrorNotification('Unable to send message: no practitioner profile');
      return;
    }

    try {
      const tenantConfig = getTenantConfig();

      // Get project ID for tenant context
      // Bot uses this to load the correct Twilio subaccount credentials
      const membership = medplum.getProjectMembership();
      const projectId = membership?.project?.reference?.replace('Project/', '') || '';

      // Call bot to send message via Twilio
      // Bot workflow:
      // 1. Validate user belongs to project
      // 2. Load Twilio subaccount credentials from Project secrets
      // 3. Load Messaging Service SID for this tenant
      // 4. Send message via tenant's Twilio subaccount
      // 5. Create FHIR Communication resource
      const result = await medplum.executeBot(
        { system: tenantConfig.botIdentifierSystem, value: 'twilio-send-message' },
        {
          patientId,
          channel,
          message,
          encounterId,
          practitionerId: currentPractitioner.id,
          organizationId: currentOrganization?.id,
          // Tenant context for subaccount selection
          projectId,
        },
        'application/json'
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      showSuccessNotification('Message sent');

      // Refresh conversations to show the new message
      await fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      showErrorNotification('Failed to send message');
      throw error;
    }
  }, [currentPractitioner?.id, currentOrganization?.id, medplum, fetchConversations]);

  const markAsRead = useCallback(async (communicationIds: string[]) => {
    try {
      // Update each communication to completed status
      for (const id of communicationIds) {
        await medplum.patchResource('Communication', id, [
          { op: 'replace', path: '/status', value: 'completed' },
        ]);
      }

      // Refresh conversations
      await fetchConversations();
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }, [medplum, fetchConversations]);

  /**
   * Send an internal chat message with proper CareComm category coding
   * This creates a FHIR Communication resource directly (no bot needed for internal chat)
   */
  const sendChatMessage = useCallback(async (
    patientId: string,
    message: string,
    threadRef?: Reference<Communication>,
    encounterId?: string
  ): Promise<Communication> => {
    if (!currentPractitioner?.id) {
      throw new Error('Unable to send message: no practitioner profile');
    }

    try {
      // Auto-link to encounter if not explicitly provided
      const linkedEncounterId = await getAutoLinkedEncounterId(
        medplum,
        patientId,
        encounterId,
        currentOrganization?.id
      );

      // Build sender display name
      const senderName = currentPractitioner.name?.[0]
        ? formatHumanName(currentPractitioner.name[0])
        : 'Provider';

      // Create Communication resource with CareComm category
      const communication = await medplum.createResource<Communication>({
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              {
                system: CARECOMM_CATEGORY_SYSTEM,
                code: 'chat',
                display: 'Internal Chat',
              },
            ],
          },
        ],
        subject: { reference: `Patient/${patientId}` },
        encounter: linkedEncounterId
          ? { reference: `Encounter/${linkedEncounterId}` }
          : undefined,
        sender: {
          reference: getReferenceString(currentPractitioner),
          display: senderName,
        },
        recipient: [{ reference: `Patient/${patientId}` }],
        partOf: threadRef ? [threadRef] : undefined,
        payload: [
          {
            contentString: message.trim(),
          },
        ],
        sent: new Date().toISOString(),
      });

      // Refresh conversations to show the new message
      await fetchConversations();

      return communication;
    } catch (error) {
      console.error('Failed to send chat message:', error);
      showErrorNotification('Failed to send message');
      throw error;
    }
  }, [currentPractitioner, currentOrganization?.id, medplum, fetchConversations]);

  /**
   * Get smart encounter link for a patient
   * Used by UI to determine if encounter selection modal is needed
   */
  const getEncounterLink = useCallback(async (
    patientId: string,
    explicitEncounterId?: string
  ): Promise<SmartEncounterLinkResult> => {
    return getSmartEncounterLink(
      medplum,
      patientId,
      explicitEncounterId,
      currentOrganization?.id
    );
  }, [medplum, currentOrganization?.id]);

  // ============================================
  // Call Actions
  // ============================================

  const startCall = useCallback(async (
    patient: Patient,
    type: 'voice-call' | 'video-call',
    encounterId?: string
  ) => {
    if (!currentPractitioner?.id) {
      showErrorNotification('Unable to start call: no practitioner profile');
      return;
    }

    if (activeCall) {
      showErrorNotification('A call is already in progress');
      return;
    }

    try {
      const tenantConfig = getTenantConfig();

      // Get project ID from Medplum membership for tenant-scoped channel naming
      const membership = medplum.getProjectMembership();
      const projectId = membership?.project?.reference?.replace('Project/', '') || 'unknown';

      // Generate tenant-scoped channel name
      // Format: "{projectId}:{contextId}:{timestamp}"
      // This ensures channel isolation at the token layer
      const channelId = generateChannelName(projectId, encounterId, patient.id || '');

      // Get Agora token from bot
      // Bot performs authorization checks:
      // 1. Verify user belongs to Medplum project
      // 2. Verify Encounter/Patient is in that project
      // 3. Verify user has correct role (Practitioner)
      // 4. Issue short TTL token (5-15 minutes)
      const tokenResult = await medplum.executeBot(
        { system: tenantConfig.botIdentifierSystem, value: 'agora-token-generator' },
        {
          channelName: channelId,
          uid: currentPractitioner.id,
          role: 'publisher',
          // Authorization context for bot validation
          patientId: patient.id,
          encounterId,
          callType: type,
        },
        'application/json'
      );

      if (!tokenResult.success || !tokenResult.token) {
        throw new Error(tokenResult.error || 'Failed to get call token');
      }

      // Initialize call state
      setActiveCall({
        channelId,
        patient,
        encounterId,
        type,
        state: 'connecting',
        startTime: Date.now(),
        duration: 0,
        isMuted: false,
        isCameraOff: type === 'voice-call',
        isScreenSharing: false,
        displayMode: 'fullscreen',
        communicationId: tokenResult.communicationId,
      });

      // Initialize Agora SDK and join channel
      // Agora requires numeric UID - parse from bot response or use 0
      const agoraUid = tokenResult.uid ? parseInt(tokenResult.uid, 10) : 0;

      await agoraService.joinCall(
        {
          appId: tokenResult.appId,
          channelName: channelId,
          token: tokenResult.token,
          uid: agoraUid,
          callType: type,
          localVideoContainerId: type === 'video-call' ? 'carecomm-local-video' : undefined,
          remoteVideoContainerId: type === 'video-call' ? 'carecomm-remote-video' : undefined,
        },
        {
          onConnectionStateChange: (state) => {
            console.log('[CareComm] Agora connection state:', state);
            if (state === 'connected') {
              setActiveCall((prev) =>
                prev ? { ...prev, state: 'connected' } : null
              );
              // Start call timer when connected
              if (!callTimerRef.current) {
                callTimerRef.current = setInterval(() => {
                  setActiveCall((prev) =>
                    prev ? { ...prev, duration: prev.duration + 1 } : null
                  );
                }, 1000);
              }
            } else if (state === 'disconnected' || state === 'failed') {
              setActiveCall((prev) =>
                prev ? { ...prev, state: 'ended' } : null
              );
            }
          },
          onUserJoined: (uid) => {
            console.log('[CareComm] Remote user joined:', uid);
          },
          onUserLeft: (uid, reason) => {
            console.log('[CareComm] Remote user left:', uid, reason);
          },
          onUserPublished: (uid, mediaType) => {
            console.log('[CareComm] Remote user published:', uid, mediaType);
            // Attach the remote video. The overlay's playRemoteVideo effect
            // only runs on the initial `connected` transition, which happens
            // before the patient joins (and again does NOT fire when the
            // patient leaves + rejoins). Re-attach here, with one short
            // retry to ride out any DOM/track-attachment race.
            if (mediaType === 'video') {
              const attach = (): void => {
                try {
                  agoraService.playRemoteVideo('carecomm-remote-video');
                } catch (err) {
                  console.warn('[CareComm] playRemoteVideo failed:', err);
                }
              };
              attach();
              setTimeout(attach, 250);
            }
          },
          onError: (error) => {
            console.error('[CareComm] Agora error:', error);
            showErrorNotification(`Call error: ${error.message}`);
          },
        }
      );

    } catch (error) {
      console.error('Failed to start call:', error);
      // Show the user-friendly error message from agoraService
      const errorMessage = error instanceof Error ? error.message : 'Failed to start call';
      showErrorNotification(errorMessage);
      setActiveCall(null);
      // Don't re-throw - we've handled the error by showing notification and clearing state
    }
  }, [currentPractitioner?.id, activeCall, medplum]);

  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      // TODO: Accept call via Agora
      setActiveCall({
        channelId: incomingCall.channelId,
        patient: incomingCall.patient,
        type: incomingCall.type,
        state: 'connected',
        startTime: Date.now(),
        duration: 0,
        isMuted: false,
        isCameraOff: incomingCall.type === 'voice-call',
        isScreenSharing: false,
        displayMode: 'fullscreen',
      });

      setIncomingCall(null);

      // Start call timer
      callTimerRef.current = setInterval(() => {
        setActiveCall((prev) =>
          prev ? { ...prev, duration: prev.duration + 1 } : null
        );
      }, 1000);
    } catch (error) {
      console.error('Failed to accept call:', error);
      showErrorNotification('Failed to accept call');
    }
  }, [incomingCall]);

  const rejectCall = useCallback(() => {
    // TODO: Reject call via Agora signaling
    setIncomingCall(null);
  }, []);

  const endCall = useCallback(async () => {
    if (!activeCall) return;

    try {
      // Stop call timer
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }

      // Create Communication record for the call
      const tenantConfig = getTenantConfig();

      await medplum.createResource<Communication>({
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              {
                system: CARECOMM_CATEGORY_SYSTEM,
                code: activeCall.type,
                display: activeCall.type === 'video-call' ? 'Video Call' : 'Voice Call',
              },
            ],
          },
        ],
        subject: { reference: `Patient/${activeCall.patient.id}` },
        encounter: activeCall.encounterId
          ? { reference: `Encounter/${activeCall.encounterId}` }
          : undefined,
        sender: { reference: `Practitioner/${currentPractitioner?.id}` },
        recipient: [{ reference: `Patient/${activeCall.patient.id}` }],
        payload: [
          {
            contentString: `${activeCall.type === 'video-call' ? 'Video' : 'Voice'} call - ${formatDuration(activeCall.duration)}`,
          },
        ],
        sent: new Date(activeCall.startTime).toISOString(),
        received: new Date().toISOString(),
        extension: [
          {
            url: CARECOMM_EXTENSION_URLS.agoraChannelId,
            valueString: activeCall.channelId,
          },
          {
            url: CARECOMM_EXTENSION_URLS.callDurationSeconds,
            valueInteger: activeCall.duration,
          },
        ],
      });

      // Mark the bot-created in-progress Communication as completed with an
      // audit reason so we can report on call outcomes. This is the
      // happy-path cleanup; pagehide + Agora-disconnect handlers cover the
      // failure modes (tab close, network drop, etc.).
      try {
        const liveComms = await medplum.searchResources('Communication', {
          subject: `Patient/${activeCall.patient.id}`,
          status: 'in-progress',
          _count: '20',
        });
        await Promise.all(
          liveComms
            .filter((c) =>
              c.extension?.some(
                (e) =>
                  e.url === CARECOMM_EXTENSION_URLS.agoraChannelId &&
                  e.valueString === activeCall.channelId
              )
            )
            .map((c) =>
              patchCommunicationOutcome(medplum, c.id as string, CARECOMM_OUTCOMES.completedNormally)
            )
        );
      } catch (patchErr) {
        console.warn('Could not mark in-progress call Communication as completed:', patchErr);
      }

      // Leave Agora channel
      try {
        await agoraService.leaveCall();
      } catch (leaveError) {
        console.warn('Error leaving Agora channel:', leaveError);
      }

      setActiveCall(null);

      showSuccessNotification('Call ended');

      // Refresh conversations
      await fetchConversations();
    } catch (error) {
      console.error('Failed to end call:', error);
      setActiveCall(null);
    }
  }, [activeCall, currentPractitioner?.id, medplum, fetchConversations]);

  // Resume an in-progress call on mount/refresh. After a page refresh the
  // provider loses activeCall state but the Communication on the server is
  // still status=in-progress (and the patient's Agora connection often still
  // sees the channel as live). Find any recent in-progress call this
  // practitioner started, mint a fresh token, and rejoin the same channel.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!currentPractitioner?.id || activeCall || resumedRef.current) return;
    let cancelled = false;
    resumedRef.current = true;

    (async () => {
      try {
        const sinceIso = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const comms = await medplum.searchResources('Communication', {
          sender: `Practitioner/${currentPractitioner.id}`,
          status: 'in-progress',
          _lastUpdated: `gt${sinceIso}`,
          _sort: '-_lastUpdated',
          _count: '5',
        });
        if (cancelled) return;

        const liveComm = comms.find((c) =>
          c.extension?.some((e) => e.url === CARECOMM_EXTENSION_URLS.agoraChannelId)
        );
        if (!liveComm) {
          resumedRef.current = false; // nothing to resume; allow re-check later
          return;
        }

        const channelId = liveComm.extension?.find(
          (e) => e.url === CARECOMM_EXTENSION_URLS.agoraChannelId
        )?.valueString;
        const code = liveComm.category?.[0]?.coding?.find(
          (c) => c.system === CARECOMM_CATEGORY_SYSTEM
        )?.code;
        const callType: 'voice-call' | 'video-call' = code === 'voice-call' ? 'voice-call' : 'video-call';
        const patientRef = liveComm.subject?.reference;
        const encounterRef = liveComm.encounter?.reference;
        if (!channelId || !patientRef?.startsWith('Patient/')) {
          resumedRef.current = false;
          return;
        }

        const patient = (await medplum.readReference({ reference: patientRef })) as Patient;
        if (cancelled) return;

        const tenantConfig = getTenantConfig();
        const tokenResult = await medplum.executeBot(
          { system: tenantConfig.botIdentifierSystem, value: 'agora-token-generator' },
          {
            channelName: channelId,
            uid: currentPractitioner.id,
            role: 'publisher',
            patientId: patient.id,
            encounterId: encounterRef?.replace('Encounter/', ''),
            callType,
          },
          'application/json'
        );
        if (cancelled) return;
        if (!tokenResult?.success || !tokenResult.token) {
          console.warn('[CareComm] Could not resume call: token request failed', tokenResult?.error);
          resumedRef.current = false;
          return;
        }

        setActiveCall({
          channelId,
          patient,
          encounterId: encounterRef?.replace('Encounter/', ''),
          type: callType,
          state: 'connecting',
          startTime: Date.now(),
          duration: 0,
          isMuted: false,
          isCameraOff: callType === 'voice-call',
          isScreenSharing: false,
          displayMode: 'fullscreen',
          communicationId: liveComm.id,
        });

        await agoraService.joinCall(
          {
            appId: tokenResult.appId,
            channelName: channelId,
            token: tokenResult.token,
            uid: 0,
            callType,
            localVideoContainerId: callType === 'video-call' ? 'carecomm-local-video' : undefined,
            remoteVideoContainerId: callType === 'video-call' ? 'carecomm-remote-video' : undefined,
          },
          {
            onConnectionStateChange: (state) => {
              if (state === 'connected') {
                setActiveCall((prev) => (prev ? { ...prev, state: 'connected' } : null));
                if (!callTimerRef.current) {
                  callTimerRef.current = setInterval(() => {
                    setActiveCall((prev) => (prev ? { ...prev, duration: prev.duration + 1 } : null));
                  }, 1000);
                }
              } else if (state === 'disconnected' || state === 'failed') {
                setActiveCall((prev) => (prev ? { ...prev, state: 'ended' } : null));
              }
            },
            onUserPublished: (_uid, mediaType) => {
              if (mediaType === 'video') {
                const attach = (): void => {
                  try {
                    agoraService.playRemoteVideo('carecomm-remote-video');
                  } catch (err) {
                    console.warn('[CareComm] playRemoteVideo (resume) failed:', err);
                  }
                };
                attach();
                setTimeout(attach, 250);
              }
            },
          }
        );
      } catch (err) {
        console.warn('[CareComm] resume-on-mount failed:', err);
        resumedRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentPractitioner?.id, activeCall, medplum]);

  // Hardening: clean up the signaling Communication when the provider's
  // browser leaves the page (tab close, navigation away, browser quit).
  // React unmount cleanup runs too late — the page is being destroyed and
  // any async fetch is cancelled. `fetch({ keepalive: true })` survives.
  useEffect(() => {
    const commId = activeCall?.communicationId;
    if (!commId) return;

    const handler = (): void => {
      patchCommunicationOnUnload({
        medplum,
        communicationId: commId,
        outcome: CARECOMM_OUTCOMES.abandonedProvider,
      });
    };

    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('pagehide', handler);
    };
  }, [activeCall?.communicationId, medplum]);

  // Hardening: if Agora reports disconnected/failed and the user didn't
  // initiate a clean hangup, treat it as an abandoned call and complete
  // the Communication. The connection-state-change callback on the agora
  // service flips activeCall.state to 'ended' just before this fires.
  const lastEndedCommRef = useRef<string | null>(null);
  useEffect(() => {
    const commId = activeCall?.communicationId;
    if (!commId) return;
    if (activeCall?.state !== 'ended') return;
    if (lastEndedCommRef.current === commId) return;
    lastEndedCommRef.current = commId;

    patchCommunicationOutcome(medplum, commId, CARECOMM_OUTCOMES.abandonedProvider).catch((err) =>
      console.warn('Could not mark abandoned call Communication as completed:', err)
    );
  }, [activeCall?.communicationId, activeCall?.state, medplum]);

  const toggleMute = useCallback(async () => {
    try {
      const newMutedState = await agoraService.toggleMute();
      setActiveCall((prev) =>
        prev ? { ...prev, isMuted: newMutedState } : null
      );
    } catch (error) {
      console.error('Failed to toggle mute:', error);
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    try {
      // Pass the container ID so video can be replayed when camera turns back on
      const newCameraOffState = await agoraService.toggleCamera('carecomm-local-video');
      setActiveCall((prev) =>
        prev ? { ...prev, isCameraOff: newCameraOffState } : null
      );
    } catch (error) {
      console.error('Failed to toggle camera:', error);
    }
  }, []);

  const toggleScreenShare = useCallback(async () => {
    if (!activeCall) return;
    try {
      if (activeCall.isScreenSharing) {
        await agoraService.stopScreenShare('carecomm-local-video');
      } else {
        await agoraService.startScreenShare('carecomm-local-video');
      }
      setActiveCall((prev) =>
        prev ? { ...prev, isScreenSharing: !prev.isScreenSharing } : null
      );
    } catch (error) {
      console.error('Failed to toggle screen share:', error);
    }
  }, [activeCall]);

  // ============================================
  // PiP Mode Actions
  // ============================================

  const setCallDisplayMode = useCallback((mode: CallDisplayMode) => {
    // On mobile, PiP mode is not available - use minimized instead
    const effectiveMode = (mode === 'pip' && isMobile) ? 'minimized' : mode;
    setActiveCall((prev) => (prev ? { ...prev, displayMode: effectiveMode } : null));
  }, [isMobile]);

  const enterPiPMode = useCallback(() => {
    // On mobile, use minimized mode instead of PiP (PiP is not available on mobile)
    if (isMobile) {
      setCallDisplayMode('minimized');
    } else {
      setCallDisplayMode('pip');
    }
  }, [setCallDisplayMode, isMobile]);

  const exitPiPMode = useCallback(() => {
    setCallDisplayMode('fullscreen');
  }, [setCallDisplayMode]);

  const minimizeCall = useCallback(() => {
    setCallDisplayMode('minimized');
  }, [setCallDisplayMode]);

  // Auto-switch to PiP mode when user navigates to a different page during a call
  const previousLocationRef = useRef(location.pathname);
  useEffect(() => {
    // Only trigger if location actually changed
    if (previousLocationRef.current !== location.pathname) {
      previousLocationRef.current = location.pathname;

      // If there's an active call in fullscreen mode, switch to PiP
      if (activeCall && activeCall.displayMode === 'fullscreen') {
        console.log('[CareComm] Route changed during active call, switching to PiP mode');
        setCallDisplayMode('pip');
      }
    }
  }, [location.pathname, activeCall, setCallDisplayMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // ============================================
  // Context Value
  // ============================================

  const value = useMemo(
    (): CareCommContextValue => ({
      settings,
      isEnabled,
      isPanelOpen,
      openPanel,
      closePanel,
      selectedPatientId,
      setSelectedPatientId,
      conversations,
      unreadCount,
      isLoadingConversations,
      refreshConversations: fetchConversations,
      sendMessage,
      sendChatMessage,
      markAsRead,
      getEncounterLink,
      activeCall,
      incomingCall,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleCamera,
      toggleScreenShare,
      setCallDisplayMode,
      enterPiPMode,
      exitPiPMode,
      minimizeCall,
      currentPractitioner,
    }),
    [
      settings,
      isEnabled,
      isPanelOpen,
      openPanel,
      closePanel,
      selectedPatientId,
      setSelectedPatientId,
      conversations,
      unreadCount,
      isLoadingConversations,
      fetchConversations,
      sendMessage,
      sendChatMessage,
      markAsRead,
      getEncounterLink,
      activeCall,
      incomingCall,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleCamera,
      toggleScreenShare,
      setCallDisplayMode,
      enterPiPMode,
      exitPiPMode,
      minimizeCall,
      currentPractitioner,
    ]
  );

  return (
    <CareCommContext.Provider value={value}>
      {children}
    </CareCommContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useCareComm(): CareCommContextValue {
  const context = useContext(CareCommContext);
  if (!context) {
    throw new Error('useCareComm must be used within a CareCommProvider');
  }
  return context;
}

// ============================================
// Helpers
// ============================================

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}
